/**
 * TimelinePanel — chronological activity log for a task.
 *
 * Displays: task creation, status changes, assignee changes,
 * deadline changes, checklist actions, estimated time updates.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../../lib/api';
import { formatRelative } from '../../../lib/format';

// ── Event configuration ───────────────────────────────────────────────────────

const STATUS_LABEL = {
  open:        'Not Started',
  in_progress: 'In Progress',
  blocked:     'Blocked',
  done:        'Completed',
  cancelled:   'Cancelled',
};

function fmtStatus(val) {
  return STATUS_LABEL[val] || val || '—';
}

function fmtMinutes(mins) {
  if (!mins || mins === '0') return '0 h';
  const m = Number(mins);
  if (isNaN(m)) return mins;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return h > 0 ? (rem > 0 ? `${h}h ${rem}m` : `${h}h`) : `${m}m`;
}

const EVENT_CONFIG = {
  created: {
    icon: '🆕',
    label: () => 'Task created',
    color: '#1a472a',
  },
  status_changed: {
    icon: '🔄',
    label: (e) => `Status changed: ${fmtStatus(e.old_value)} → ${fmtStatus(e.new_value)}`,
    color: '#0969a8',
  },
  assignee_changed: {
    icon: '👤',
    label: (e, memberName) =>
      `Assignee changed: ${memberName(e.old_value)} → ${memberName(e.new_value)}`,
    color: '#6f42c1',
  },
  deadline_changed: {
    icon: '📅',
    label: (e) =>
      `Deadline changed: ${e.old_value || '(none)'} → ${e.new_value || '(none)'}`,
    color: '#d4a93f',
  },
  checklist_item_added: {
    icon: '➕',
    label: (e) => `Checklist item added: "${e.new_value}"`,
    color: '#2e7d32',
  },
  checklist_item_checked: {
    icon: '✅',
    label: (e) => `Checked: "${e.note}"`,
    color: '#2e7d32',
  },
  checklist_item_unchecked: {
    icon: '☑️',
    label: (e) => `Unchecked: "${e.note}"`,
    color: '#856404',
  },
  checklist_item_edited: {
    icon: '✏️',
    label: (e) => `Checklist item edited: "${e.old_value}" → "${e.new_value}"`,
    color: '#555',
  },
  checklist_item_deleted: {
    icon: '🗑️',
    label: (e) => `Checklist item removed: "${e.old_value}"`,
    color: '#c0392b',
  },
  estimated_time_set: {
    icon: '⏱️',
    label: (e) =>
      `Estimated time updated: ${fmtMinutes(e.old_value)} → ${fmtMinutes(e.new_value)}`,
    color: '#555',
  },
  time_logged: {
    icon: '🕐',
    label: (e) => {
      const mins = Number(e.new_value);
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      const dur = h > 0 ? `${h}h ${m > 0 ? m + 'm' : ''}`.trim() : `${m}m`;
      return e.note ? `Time logged: ${dur} — ${e.note}` : `Time logged: ${dur}`;
    },
    color: '#0969a8',
  },
  quality_reviewed: {
    icon: '⭐',
    label: (e) => {
      const stars = '★'.repeat(Number(e.new_value)) + '☆'.repeat(5 - Number(e.new_value));
      const base = e.old_value ? `Quality review updated: ${stars}` : `Quality review posted: ${stars}`;
      return e.note ? `${base} — ${e.note}` : base;
    },
    color: '#d4a93f',
  },
};

const DEFAULT_CONFIG = {
  icon: '📝',
  label: (e) => e.event_type.replace(/_/g, ' '),
  color: '#555',
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function TimelinePanel({ appId, taskId, members }) {
  const api = useApi();
  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.listTimeline(appId, taskId);
      setEvents(r.items || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [api, appId, taskId]);

  useEffect(() => { load(); }, [load]);

  const memberName = (idStr) => {
    const id = Number(idStr);
    if (!id) return idStr || '—';
    const u = (members || []).find((m) => m.user_id === id);
    if (!u) return `User #${id}`;
    return [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || `User #${id}`;
  };

  const actorName = (userId) => {
    if (!userId) return 'System';
    const u = (members || []).find((m) => m.user_id === userId);
    if (!u) return `User #${userId}`;
    return [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email;
  };

  if (loading) return <section className="task-section"><p className="muted">Loading timeline…</p></section>;
  if (error)   return <section className="task-section"><div className="banner banner-error">{error}</div></section>;

  return (
    <section className="task-section">
      <h2>Timeline</h2>

      {events.length === 0 ? (
        <p className="muted">No activity recorded yet.</p>
      ) : (
        <ol className="task-timeline">
          {events.map((ev) => {
            const cfg   = EVENT_CONFIG[ev.event_type] || DEFAULT_CONFIG;
            const label = cfg.label(ev, memberName);
            return (
              <li key={ev.id} className="timeline-event">
                <span className="timeline-icon" style={{ color: cfg.color }}>{cfg.icon}</span>
                <div className="timeline-body">
                  <span className="timeline-label">{label}</span>
                  <span className="timeline-meta">
                    <span className="timeline-actor">{actorName(ev.user_id)}</span>
                    {' · '}
                    <span className="timeline-time muted small" title={ev.created_at}>
                      {formatRelative(ev.created_at)}
                    </span>
                  </span>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
